import { PlanSubmissionStatus } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import {
  PlanSubmitBlockReason,
  ProcurementPlanSubmitErrorCategory,
} from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

import { displayableBreakdownRowLimit } from '~/models/planSubmission/errorDetail'
import type { InterpolatedMessage } from '~/models/planSubmission/interpolatedMessage'
import {
  SEND_FAILED_CAUSE_SHORT_KEY,
  resolveSendFailedCause,
  resolveSendFailedGuidanceText,
} from '~/models/planSubmission/sendFailedCause'
import { blockReasonView } from '~/pages/ProcurementPlanDetail/models/blockReasonView'
import type { SubmitDisabledReason } from '~/pages/ProcurementPlanDetail/models/canSubmit'

/**
 * 提出情報バー直下の誘導メッセージ (AC-PLC-020-2 の位置) の定義。
 *
 * 「理由・状態 → 見出し・文言とボタン」の対応表で持つ。状態や原因の行を足す時は、
 * `resolveSubmitGuidance` に 1 分岐足す (表示コンポーネントは変えない)。
 */
export type SubmitGuidanceAction =
  | {
      labelKey: string
      /** 'openLatest': `planId` (= latestPlanSubmissionId) の詳細を開く (AC-PLC-055 / 056)。 */
      kind: 'openLatest'
      planId: string
    }
  | {
      labelKey: string
      /**
       * 'submit': 提出ボタンと同じ提出確認ダイアログを開く (AC-PLC-029。AC-PLC-020-2 の
       * 「提出する」(未提出)・「再提出」(送信失敗))。
       */
      kind: 'submit'
      /** 提出ボタンと同じ無効条件 (空コマ・閲覧のみ・提出中) の時 true。 */
      disabled: boolean
      /** 無効の時にツールチップで出す理由 (ヘッダーの「提出」 と同じ文)。理由の文が無い無効は undefined。 */
      disabledReason?: SubmitDisabledReason
    }
  | {
      labelKey: string
      /**
       * 'scrollToTable': 計画のテーブルへスクロールする (AC-PLC-020-2 の差戻し・提出失敗の
       * 「修正して再提出」。design `renderNextAction` の resume。再提出はヘッダーの「提出」)。
       */
      kind: 'scrollToTable'
    }
  | {
      labelKey: string
      /** 'showMismatch': テーブル上部の不整合バナーへスクロールする (AC-PLC-020-2 不整合)。 */
      kind: 'showMismatch'
    }
  | {
      labelKey: string
      /**
       * 'recreate': 当該計画の対象日・エリア・BG を初期値にして計画作成ダイアログを
       * 開く (AC-PLC-020-2 作成失敗、AC-PLC-056 上記以外〈MISSING_BASELINE_BOXES〉、AC-PLC-038)。
       */
      kind: 'recreate'
      /** 「計画を作り直す」 を押せない (閲覧のみ・計画の対象日が無い) 時 true。 */
      disabled: boolean
    }

export type SubmitGuidance = {
  /** 見出し。`causeKey` (原因の短い区別) があれば `{{cause}}` に入れる。 */
  title?: { key: string; causeKey?: string }
  /** 本文。`rejectionCodes` があれば locale の区切りで連結して `{{codes}}` に入れる。 */
  message: InterpolatedMessage
  /** DS Alert の status。 */
  status: 'warning' | 'error'
  action?: SubmitGuidanceAction
}

export type SubmitGuidanceInput = {
  submitBlockReason: PlanSubmitBlockReason
  latestPlanSubmissionId: string | undefined
  status: PlanSubmissionStatus
  errorCategory: ProcurementPlanSubmitErrorCategory
  /** 内訳の件数の上限 (AC-PLC-064)。上限超過の作成失敗の時だけ set。 */
  breakdownRowLimit?: number
  credentialRejectionResultCodes: readonly string[]
  /**
   * ヘッダーの提出ボタンを押せるか (`resolveSubmitAvailability` の `enabled`)。「提出する」「再提出」 も
   * 同じ条件に従う。
   */
  isSubmitActionEnabled: boolean
  /** 提出ボタンを押せない時にヘッダーが出す理由の文 (無ければ undefined)。 */
  submitDisabledReason: SubmitDisabledReason | undefined
  /**
   * 取引相手間ミスマッチがあり、そのすべてを解消済みにした計画か
   * (`submission-management.md` §7)。不整合でも誘導を出さない (AC-PLC-020-2)。
   */
  isCounterpartyMismatchResolved: boolean
  /**
   * 「計画を作り直す」 を押せるか。閲覧のみ (完了済みワークフローケース、一覧の「計画作成」
   * と同じ無効条件) と、計画の対象日が無い (ダイアログの初期値が作れない) 時は false。
   */
  isRecreateEnabled: boolean
}

/**
 * 提出を止める理由 (AC-PLC-055 / 056 / 062) があればその誘導を出し、ステータス連動の誘導は
 * 出さない (AC-PLC-020-2 ※: 押しても拒否される「提出する」「再提出」 を出さない)。
 * 理由なし (UNSPECIFIED) ならステータス連動の誘導 (AC-PLC-020-2) を出す。
 * ユーザー操作待ちでない状態 (作成中・処理中・提出済・受領済等) は undefined =
 * 誘導メッセージを出さない。
 * 表示と動作を同じ入力から決めるため、ボタンの有無と無効はここで解決する。
 */
export function resolveSubmitGuidance(
  input: SubmitGuidanceInput,
): SubmitGuidance | undefined {
  if (input.submitBlockReason === PlanSubmitBlockReason.UNSPECIFIED) {
    return resolveStatusGuidance(input)
  }
  return resolveBlockReasonGuidance(input)
}

/**
 * ステータス連動の誘導 (AC-PLC-020-2)。Alert の status は design (`renderNextAction`)
 * の variant に合わせ、未提出・差戻しは warning、それ以外は error。
 */
function resolveStatusGuidance(
  input: SubmitGuidanceInput,
): SubmitGuidance | undefined {
  switch (input.status) {
    case PlanSubmissionStatus.GENERATED:
      return {
        message: {
          key: 'page:procurementPlanDetail.submitGuidance.unsubmitted',
        },
        status: 'warning',
        action: submitAction(
          'page:procurementPlanDetail.submitGuidance.submit',
          input,
        ),
      }
    case PlanSubmissionStatus.REJECTED:
      return {
        title: {
          key: 'page:procurementPlanDetail.submitGuidance.rejectedTitle',
        },
        message: { key: 'page:procurementPlanDetail.submitGuidance.rejected' },
        status: 'warning',
        action: FIX_AND_RESUBMIT_ACTION,
      }
    case PlanSubmissionStatus.INCONSISTENT:
      // 解消後は不整合のハイライトが無く「修正のうえ再提出」 が当てはまらない
      // (`submission-management.md` Discussion「解消後の提出ステータスと誘導メッセージ」)
      if (input.isCounterpartyMismatchResolved) return undefined
      return {
        message: {
          key: 'page:procurementPlanDetail.submitGuidance.inconsistent',
        },
        status: 'error',
        action: {
          labelKey: 'page:procurementPlanDetail.submitGuidance.showMismatch',
          kind: 'showMismatch',
        },
      }
    case PlanSubmissionStatus.GENERATION_FAILED: {
      // OCCTO の 1 ファイルの上限 (AC-PLC-080) は、原因を区別する category が
      // できた時にここで分ける。
      const breakdownRowLimit = displayableBreakdownRowLimit(input)
      if (breakdownRowLimit !== undefined) {
        return {
          message: {
            key: 'page:procurementPlanDetail.submitGuidance.breakdownLimitExceeded',
            limit: breakdownRowLimit,
          },
          status: 'error',
          action: recreateAction(input),
        }
      }
      return {
        message: {
          key: 'page:procurementPlanDetail.submitGuidance.generationFailed',
        },
        status: 'error',
        action: recreateAction(input),
      }
    }
    case PlanSubmissionStatus.SUBMIT_FAILED:
      return resolveSubmitFailedGuidance(input)
    case PlanSubmissionStatus.SEND_FAILED:
      return resolveSendFailedGuidance(input)
    default:
      return undefined
  }
}

/** 作成失敗の「計画を作り直す」 (AC-PLC-020-2。原因によらず同じ動作・無効条件)。 */
function recreateAction(input: SubmitGuidanceInput): SubmitGuidanceAction {
  return {
    labelKey: 'page:procurementPlanDetail.submitGuidance.recreate',
    kind: 'recreate',
    disabled: !input.isRecreateEnabled,
  }
}

/** 差戻し・提出失敗の「修正して再提出」: 修正箇所を見るためテーブルへ移る (提出はヘッダーの「提出」)。 */
const FIX_AND_RESUBMIT_ACTION: SubmitGuidanceAction = {
  labelKey: 'page:procurementPlanDetail.submitGuidance.fixAndResubmit',
  kind: 'scrollToTable',
}

function submitAction(
  labelKey: string,
  input: SubmitGuidanceInput,
): SubmitGuidanceAction {
  return {
    labelKey,
    kind: 'submit',
    disabled: !input.isSubmitActionEnabled,
    disabledReason: input.isSubmitActionEnabled
      ? undefined
      : input.submitDisabledReason,
  }
}

/**
 * 提出失敗 (AC-PLC-020-2)。全コマ GC 済み (AC-PLC-037-2) を先に判定し、ボタンを
 * 置かない (元データ・マスタを直しても送れるコマが無いため)。それ以外は
 * 「修正して再提出」。OCCTO の 1 ファイルの上限 (AC-PLC-080) は、原因を区別する
 * errorCategory ができた時にここで分ける。
 */
function resolveSubmitFailedGuidance(
  input: SubmitGuidanceInput,
): SubmitGuidance {
  if (
    input.errorCategory ===
    ProcurementPlanSubmitErrorCategory.GATE_CLOSED_ALL_KOMA
  ) {
    return {
      message: {
        key: 'page:procurementPlanDetail.submitGuidance.gateClosedAllKoma',
      },
      status: 'error',
    }
  }
  return {
    message: { key: 'page:procurementPlanDetail.submitGuidance.submitFailed' },
    status: 'error',
    action: FIX_AND_RESUBMIT_ACTION,
  }
}

function resolveSendFailedGuidance(input: SubmitGuidanceInput): SubmitGuidance {
  const cause = resolveSendFailedCause(input.errorCategory)
  return {
    title: {
      key: 'page:procurementPlanDetail.submitGuidance.sendFailedTitle',
      causeKey: SEND_FAILED_CAUSE_SHORT_KEY[cause],
    },
    message: resolveSendFailedGuidanceText(
      cause,
      input.credentialRejectionResultCodes,
    ),
    status: 'error',
    action: submitAction(
      'page:procurementPlanDetail.submitGuidance.resubmit',
      input,
    ),
  }
}

/**
 * 理由ごとの本文は `blockReasonView` の表で決める。「最新の計画を開く」は遷移先
 * (latestPlanSubmissionId) がある時だけ出す。どの理由で id を返すかは backend が決める。
 * id が無い時は表の `fallbackAction` に従う。
 */
function resolveBlockReasonGuidance(
  input: SubmitGuidanceInput,
): SubmitGuidance | undefined {
  const view = blockReasonView(input.submitBlockReason)
  if (view === undefined) return undefined
  const { latestPlanSubmissionId } = input
  let action: SubmitGuidanceAction | undefined
  if (latestPlanSubmissionId !== undefined && latestPlanSubmissionId !== '') {
    action = {
      labelKey: 'page:procurementPlanDetail.submitGuidance.openLatest',
      kind: 'openLatest',
      planId: latestPlanSubmissionId,
    }
  } else if (view.fallbackAction === 'recreate') {
    action = recreateAction(input)
  }
  return { message: { key: view.messageKey }, status: 'warning', action }
}
